import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/calendar/calendar.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { PresenceRecord, Status, type UserPresenceRecord } from '@chancenwerk/db/PresenceRecord.js';
import { Course } from '@chancenwerk/db/courses/Course.js';
import { COURSE_TITLES } from '@chancenwerk/db/courses/CourseHelper.js';
import { Weekday } from '@chancenwerk/db/enums/enums.js';
import { getAccessibleKPFIdsForUsersRole } from '@chancenwerk/db/permissions.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import type { Mitarbeiter } from '@chancenwerk/db/users/Mitarbeiter.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-image.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/fonts.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { DateTime } from 'luxon';
import { switchMap } from 'rxjs';
import { KPF } from '../../../../src/master-data-management/kpf/entities/KPF.js';

const courseSelector: BaseQLSelectionSet<Course> = {
  _id: 1,
  _class: 1,
  name: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
};

const userSelector: BaseQLSelectionSet<ChancenwerkUser> = {
  _class: 1,
  _id: 1,
  vorname: 1,
  nachname: 1,
  email: 1,
};

const userRecordSelector: BaseQLSelectionSet<UserPresenceRecord> = {
  _id: 1,
  _class: 1,
  userID: 1,
  date: 1,
  status: 1,
  comment: 1,
  excuseReason: 1,
  user: userSelector,
};

const recordSelector: BaseQLSelectionSet<PresenceRecord> = {
  _id: 1,
  _class: 1,
  date: 1,
  comment: 1,
  courseID: 1,
  schukoRecords: {
    _id: 1,
    _class: 1,
    kpfID: 1,
    date: 1,
    schukoRecords: userRecordSelector,
  },
  studentRecordsOS: userRecordSelector,
  studentRecordsUS: userRecordSelector,
  lekoRecords: userRecordSelector,
};

const EVENTS_WEEKS = 4;

/**
 * @customElement campus-page-administration-leko-attendance
 *
 * Handles the absence for LeKos that they are allowed to submit themselves.
 */
@customElement('campus-page-administration-leko-attendance')
export class CampusPageNotFound extends ChemistryLitElement {
  @state() private readonly _size = new WindowSizeController(this);
  @state() private readonly _currentUser = new RXController(this, currentUser<Mitarbeiter>());

  private readonly _kpfs = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(async user => {
        if (!user) {
          return [];
        }
        const ids = getAccessibleKPFIdsForUsersRole(Role.LEKO, user);
        const _kpfs: KPF[] = [];
        for (const id of ids) {
          const kpf = await KPF.getByID<KPF>(id)(KPF.allFields);
          if (!kpf) {
            continue;
          }
          _kpfs.push(kpf);
        }
        return _kpfs;
      }),
    ),
  );

  @state() private readonly _selectedKpf = new RXController<KPF | null>(this, null);
  @state() private readonly _selectedRecord = new RXController<PresenceRecord | null>(this, null);
  @state() private readonly _selectedUserRecord = new RXController<UserPresenceRecord | null>(this, null);
  @state() private readonly _generatedEvents = new RXController<any[]>(this, []);

  private readonly coursesFromKPF = new RXController(
    this,
    this._selectedKpf.observable.pipe(
      switchMap(kpf => (kpf ? Course.subscribeAllCoursesByKPFID(kpf._id)(courseSelector) : [])),
    ),
  );

  override connectedCallback() {
    // * if only 1 kpf is present we select it automatically
    this._kpfs.observable.subscribe(_kpfs => {
      if (_kpfs && _kpfs.length === 1) {
        this._selectedKpf.next(_kpfs[0] ?? null);
      }
    });

    this.coursesFromKPF.observable.subscribe(courses => {
      this._generatedEvents.next(this._generateEvents(courses ?? []));
    });

    this._selectedRecord.observable.subscribe(record => {
      if (!record) {
        this._selectedUserRecord.next(null);
        return;
      }

      this._selectedUserRecord.next(
        (record.lekoRecords ?? []).find(u => u.userID === this._currentUser.value?._id) ?? null,
      );
    });
    super.connectedCallback();
  }

  override render() {
    return html`<cr-flex padding="${this._size.mobile ? 'md' : 'xxl'}" space="lg">
      <cr-flex padding="md" space="md">
        <cr-h2> Abwesenheit </cr-h2>
        <cr-text> Hier kannst du dich als LeKo für Kurse als abwesend melden. </cr-text>
        <cr-dropdown-selection
          slot="right"
          clearable
          .value=${this._selectedKpf.value}
          ?flex=${!this._selectedKpf.value}
          icon="search"
          placeholder="Einsatzort"
          .selectables=${this._kpfs.value}
          .renderString=${(entry: KPF) => {
            return entry.stringRepresentation;
          }}
          @value-picked=${(e: CustomEvent<{ value: KPF }>) => {
            this._selectedKpf.next(e.detail.value);
            this._selectedRecord.next(null);
          }}
        ></cr-dropdown-selection>
        ${this._calendar()} ${this.__selectedRecord()}
      </cr-flex>
    </cr-flex>`;
  }

  private _generateEvents(courses: Course[]) {
    const start = DateTime.now().startOf('week');

    const possibleEvents: any = [];
    for (let w = 0; w < EVENTS_WEEKS + 1; w++) {
      for (const course of courses) {
        if (!course.leKoIDs.includes(this._currentUser.value?._id ?? '')) {
          continue;
        }

        const day = start
          .plus({ weeks: w })
          .set({ weekday: Object.values(Weekday).indexOf(course.weekday ?? Weekday.MONDAY) + 1 });

        const event = {
          id: course._id,
          title: `${COURSE_TITLES[course._class]} ${course.location}`,
          start: `${day.toFormat('yyyy-MM-dd')}T${course.startTime?.toFormat('HH:mm:ss')}`,
          end: `${day.toFormat('yyyy-MM-dd')}T${course.endTime?.toFormat('HH:mm:ss')}`,
          backgroundColor: this.colors.accent,
          borderColor: 'transparent',
          display: 'block',
        };

        possibleEvents.push(event);
      }
    }

    return possibleEvents;
  }

  private async _calendarEventClick(e: any) {
    this._selectedUserRecord.next(null);
    this._selectedRecord.next(
      await PresenceRecord.getPresenceRecordForCourseAndDate(
        e.detail.event.id,
        DateTime.fromISO(e.detail.event.startStr),
      )(recordSelector),
    );
  }

  private _calendar() {
    if (!this._selectedKpf.value) {
      return nothing;
    }

    return guard(
      [this._selectedKpf.value, this._generatedEvents.value],
      () => html`<x-calendar
        week
        month
        day
        .events=${this._generatedEvents.value}
        @eventClick=${this._calendarEventClick}
      >
      </x-calendar>`,
    );
  }

  private async _saveSchuKoRecord() {
    if (this._selectedRecord.value?.schukoRecords) {
      if (!this._selectedRecord.value.schukoRecords._id) {
        await this._selectedRecord.value.schukoRecords.create();
      } else {
        await this._selectedRecord.value.schukoRecords.save();
      }
    }
  }

  private async _saveRecord() {
    await this._saveSchuKoRecord();
    if (!this._selectedRecord.value?._id) {
      await this._selectedRecord.value?.create();
      this._selectedRecord.next(this._selectedRecord.value);
    } else {
      await this._selectedRecord.value.save();
    }

    void ChromiumSnackbar.show('Abwesenheit gespeichert');
    this.requestUpdate();
  }

  private async _submitExcuse() {
    if (!this._selectedUserRecord.value) {
      return;
    }

    this._selectedUserRecord.value.status = Status.EXCUSED;
    await this._saveRecord();
  }

  private __selectedRecord() {
    if (this._selectedRecord.isLoading || !this._selectedRecord.value) {
      return nothing;
    }

    return html`
      <cr-flex space="md">
        <cr-h3>Du möchtest dich für den ${this._selectedRecord.value.date.toFormat('dd. LLL yyyy')} abmelden?</cr-h3>
        <cr-text>
          <cr-text bold>Aktueller Status: </cr-text>
          <cr-text>${this._selectedUserRecord.value?.statusTranslated ?? ''}</cr-text>
        </cr-text>

        <cr-flex horizontal space="md">
          <cr-input
            placeholder="Grund (Optional)"
            .value=${this._selectedUserRecord.value?.excuseReason}
            @value-changed=${(e: ValueEvent<string>) => {
              if (!this._selectedUserRecord.value) return;
              this._selectedUserRecord.value.excuseReason = e.detail.value;
              this._selectedUserRecord.next(this._selectedUserRecord.value);
            }}
            ${css({ minWidth: '350px' })}
          ></cr-input>

          <cr-button @click=${this._submitExcuse}>Abmelden</cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }
}
